import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartColumn, faFilter, faSearch, faSpinner } from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import { showToast } from "../../components/Ui_Element/CustomToast";
import {
  fetchAnalyticsCustomerUsers,
  fetchAnalyticsCustomers,
  fetchAnalyticsEmployees,
  fetchAnalyticsOrganizations,
  fetchAnalyticsStatusOptions,
  fetchTicketAnalyticsDashboard,
  getDefaultAnalyticsFilters,
  parseAnalyticsDisplayDate,
} from "./analyticsReportingService";
import AnalyticsChartsGrid from "./components/AnalyticsChartsGrid";
import AnalyticsFilterPanel from "./components/AnalyticsFilterPanel";
import AnalyticsInsightPanels from "./components/AnalyticsInsightPanels";
import KpiSummaryCards from "./components/KpiSummaryCards";

const emptyAnalytics = {
  summary: {
    totalOrganizations: 0,
    totalCustomers: 0,
    totalCustomerUsers: 0,
    totalEmployees: 0,
    totalTickets: 0,
    totalTasks: 0,
    openTickets: 0,
    resolvedTickets: 0,
    resolutionRate: 0,
    averageResolutionHours: 0,
    averageTasksPerTicket: 0,
    activeAssignments: 0,
  },
  statusBreakdown: [],
  ticketTrend: [],
  taskTrend: [],
  ticketsByCustomer: [],
  tasksByCustomer: [],
  ticketsByOrganization: [],
  employeeWorkload: [],
  activeCustomerUsers: [],
  customerResolution: [],
  activityTimeline: [],
};

const isValidDateRange = (filters) => {
  if (!filters.startDate || !filters.endDate) return false;
  const startDate = parseAnalyticsDisplayDate(filters.startDate);
  const endDate = parseAnalyticsDisplayDate(filters.endDate);
  if (!startDate || !endDate) return false;
  return endDate >= startDate;
};

export default function AdminAnalyticsDashboardPage() {
  const [filters, setFilters] = useState(getDefaultAnalyticsFilters);
  const [organizations, setOrganizations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [statusOptions, setStatusOptions] = useState(["All"]);
  const [analytics, setAnalytics] = useState(emptyAnalytics);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [customerUsersLoading, setCustomerUsersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const runAnalytics = useCallback(async (activeFilters) => {
    if (!isValidDateRange(activeFilters)) {
      const message = "Use date format dd-mm-yyyy, and make sure end date is on or after start date.";
      setError(message);
      showToast.error(message);
      return false;
    }

    setLoading(true);
    setError("");

    try {
      const result = await fetchTicketAnalyticsDashboard(activeFilters);
      setAnalytics(result);
      setSearched(true);
      return true;
    } catch (analyticsError) {
      const message = analyticsError?.message || "Analytics dashboard could not be loaded.";
      setAnalytics(emptyAnalytics);
      setSearched(true);
      setError(message);
      showToast.error(message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const defaultFilters = getDefaultAnalyticsFilters();

    const loadFilters = async () => {
      setFiltersLoading(true);
      setLoading(true);
      setError("");

      try {
        const [organizationItems, customerItems, employeeItems, statuses] = await Promise.all([
          fetchAnalyticsOrganizations(),
          fetchAnalyticsCustomers(),
          fetchAnalyticsEmployees(),
          fetchAnalyticsStatusOptions(),
        ]);

        if (cancelled) return;

        setOrganizations(organizationItems);
        setCustomers(customerItems);
        setEmployees(employeeItems);
        setStatusOptions(statuses);

        if (!cancelled) {
          setFilters(defaultFilters);
        }

        const analyticsResult = await fetchTicketAnalyticsDashboard(defaultFilters);
        if (cancelled) return;

        setAnalytics(analyticsResult);
        setSearched(true);
      } catch {
        if (!cancelled) {
          setAnalytics(emptyAnalytics);
          setSearched(true);
          setError("Analytics filters or default dashboard data could not be loaded.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFiltersLoading(false);
        }
      }
    };

    loadFilters();

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleCustomers = useMemo(() => {
    let items = customers;

    if (filters.organizationId) {
      items = items.filter((item) => Number(item.organizationId || 0) === Number(filters.organizationId));
    }

    if (filters.employeeUserId) {
      items = items.filter((item) => String(item.empUserId || "") === String(filters.employeeUserId));
    }

    return items;
  }, [customers, filters.employeeUserId, filters.organizationId]);

  useEffect(() => {
    let cancelled = false;

    const loadCustomerUsers = async () => {
      const scopedCustomerIds = filters.customerId
        ? [Number(filters.customerId)]
        : (filters.organizationId || filters.employeeUserId)
          ? visibleCustomers.map((item) => Number(item.id)).filter((item) => item > 0)
          : [];

      if (!scopedCustomerIds.length) {
        setCustomerUsers([]);
        setCustomerUsersLoading(false);
        return;
      }

      setCustomerUsersLoading(true);

      try {
        const users = await fetchAnalyticsCustomerUsers(scopedCustomerIds);
        if (!cancelled) {
          setCustomerUsers(users);
        }
      } catch {
        if (!cancelled) {
          setCustomerUsers([]);
        }
      } finally {
        if (!cancelled) {
          setCustomerUsersLoading(false);
        }
      }
    };

    loadCustomerUsers();

    return () => {
      cancelled = true;
    };
  }, [filters.customerId, filters.employeeUserId, filters.organizationId, visibleCustomers]);

  const visibleCustomerUsers = useMemo(() => {
    if (filters.customerId) {
      return customerUsers.filter((item) => Number(item.customerId || 0) === Number(filters.customerId));
    }

    const visibleCustomerIds = new Set(visibleCustomers.map((item) => Number(item.id)));
    return customerUsers.filter((item) => visibleCustomerIds.has(Number(item.customerId || 0)));
  }, [customerUsers, filters.customerId, visibleCustomers]);

  useEffect(() => {
    if (filters.customerId && !visibleCustomers.some((item) => Number(item.id) === Number(filters.customerId))) {
      setFilters((current) => ({ ...current, customerId: "", customerUserId: "" }));
      return;
    }

    if (filters.customerUserId && !visibleCustomerUsers.some((item) => item.userId === filters.customerUserId)) {
      setFilters((current) => ({ ...current, customerUserId: "" }));
    }
  }, [filters.customerId, filters.customerUserId, visibleCustomers, visibleCustomerUsers]);

  const handleFilterChange = (key, value) => {
    setFilters((current) => {
      if (key === "organizationId") {
        return {
          ...current,
          organizationId: value,
          customerId: "",
          customerUserId: "",
        };
      }

      if (key === "employeeUserId") {
        return {
          ...current,
          employeeUserId: value,
          customerId: "",
          customerUserId: "",
        };
      }

      if (key === "customerId") {
        return {
          ...current,
          customerId: value,
          customerUserId: "",
        };
      }

      return {
        ...current,
        [key]: value,
      };
    });
  };

  const handleReset = async () => {
    const nextFilters = getDefaultAnalyticsFilters();
    setFilters(nextFilters);
    setCustomerUsers([]);
    setError("");
    const success = await runAnalytics(nextFilters);
    if (success) {
      setIsFilterPanelOpen(false);
    }
  };

  const handleRunAnalytics = async () => {
    const success = await runAnalytics(filters);
    if (success) {
      setIsFilterPanelOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <Breadcrumb pageName="Admin Analytics Dashboard" currentPage="Admin Analytics Dashboard" />

      <div className="mt-4 overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm">
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.22),_transparent_24%),radial-gradient(circle_at_88%_18%,_rgba(16,185,129,0.18),_transparent_20%),linear-gradient(135deg,#081120,#10233f_50%,#16325c)] px-5 py-6 text-white md:px-7 md:py-8">
          <div className="absolute -left-8 top-8 h-28 w-28 rounded-full bg-sky-300/10 blur-3xl" />
          <div className="absolute right-0 top-0 h-40 w-40 translate-x-8 -translate-y-8 rounded-full bg-emerald-300/10 blur-3xl" />
          <div className="absolute bottom-0 right-16 h-24 w-24 rounded-full bg-blue-300/10 blur-2xl" />
          <div className="relative flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-4xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-slate-100 shadow-sm backdrop-blur">
                <FontAwesomeIcon icon={faChartColumn} />
                Analytics & Reporting
              </div>
              <div className="mt-2 flex flex-wrap gap-3 text-xs">
                <span className="rounded-full border border-sky-300/30 bg-sky-300/12 px-3 py-1.5 font-medium text-sky-100">
                  KPI Cards
                </span>
                <span className="rounded-full border border-emerald-300/30 bg-emerald-300/12 px-3 py-1.5 font-medium text-emerald-100">
                  Bar + Pie + Line + Area
                </span>
                <span className="rounded-full border border-blue-300/30 bg-blue-300/12 px-3 py-1.5 font-medium text-blue-100">
                  Org + Customer + Employee Insights
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-4 xl:min-w-[320px] xl:max-w-[360px] xl:items-end">
              <div className="flex w-full flex-col gap-3 sm:flex-row xl:justify-end">
                <button
                  type="button"
                  onClick={() => setIsFilterPanelOpen(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-300/10 px-5 py-3 text-sm font-semibold text-cyan-50 transition hover:bg-cyan-300/16 sm:w-auto"
                >
                  <FontAwesomeIcon icon={faFilter} />
                  Report Filters
                </button>

                <button
                  type="button"
                  onClick={handleRunAnalytics}
                  disabled={loading || filtersLoading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#fbbf24,#f97316)] px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                >
                  <FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} />
                  {loading ? "Loading Analytics..." : "Run Analytics"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6 p-5 md:p-6">
          {loading && !searched ? (
            <div className="rounded-[28px] border border-slate-200 bg-white px-6 py-12 text-center text-sm text-slate-500 shadow-sm">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading analytics dashboard...
            </div>
          ) : searched ? (
            <>
              <KpiSummaryCards summary={analytics.summary} />
              <AnalyticsChartsGrid analytics={analytics} />
              <AnalyticsInsightPanels analytics={analytics} />
            </>
          ) : (
            <div className="rounded-[28px] border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-sm text-slate-500 shadow-sm">
              Run the report to load admin analytics for organizations, customers, customer users, assigned employees, tickets, and tasks.
            </div>
          )}
        </div>
      </div>

      <div className={`fixed inset-0 z-[80] ${isFilterPanelOpen ? "" : "pointer-events-none"}`}>
        <button
          type="button"
          aria-label="Close filters"
          onClick={() => setIsFilterPanelOpen(false)}
          className={`absolute inset-0 bg-slate-950/35 transition ${isFilterPanelOpen ? "opacity-100" : "opacity-0"}`}
        />

        <div
          className={`absolute right-0 top-0 h-full w-full max-w-xl transform p-3 transition duration-300 sm:p-5 ${isFilterPanelOpen ? "translate-x-0" : "translate-x-full"
            }`}
        >
          <div className="h-full overflow-y-auto rounded-[32px] bg-slate-50 shadow-2xl">
            <div className="p-3 sm:p-5">
              <AnalyticsFilterPanel
                filters={filters}
                statusOptions={statusOptions}
                organizations={organizations}
                employees={employees}
                customers={visibleCustomers}
                customerUsers={visibleCustomerUsers}
                loading={loading || filtersLoading}
                customerUsersLoading={customerUsersLoading}
                error={error}
                onFilterChange={handleFilterChange}
                onReset={handleReset}
                onSubmit={handleRunAnalytics}
                onClose={() => setIsFilterPanelOpen(false)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
